import { useState } from 'react';
import { Box, Button, CircularProgress, Tooltip, Typography } from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import ConfirmDialog from '../common/ConfirmDialog';
import type { CreditImportBatch } from '../../services/creditImportService';
import { pluralActivities } from '../../utils/creditImportValidation';
import { fileImportSourceLabel } from './fileImportSources';

interface Props {
  batches: CreditImportBatch[];
  loading: boolean;
  /** Resolves true when the undo succeeded (the parent surfaces failures itself). */
  onUndo: (batch: CreditImportBatch) => Promise<boolean>;
}

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * The last few spreadsheet imports with Undo: the way back after the success snackbar is gone.
 * Undo here can remove a whole batch from weeks ago, so it confirms with the same dialog the ledger
 * uses for deletes.
 */
export default function RecentImportsList({ batches, loading, onUndo }: Props) {
  const [pending, setPending] = useState<CreditImportBatch | null>(null);
  const [busy, setBusy] = useState(false);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }
  if (batches.length === 0) return null;

  const confirmUndo = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      await onUndo(pending);
    } finally {
      setBusy(false);
      setPending(null);
    }
  };

  return (
    <Box component="section" aria-labelledby="recent-imports-heading">
      <Typography id="recent-imports-heading" variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Recent imports
      </Typography>
      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {batches.map((b) => {
          const who = b.uploadedBySelf ? '' : ` · by ${b.uploadedByName}${b.targetUserName ? ` for ${b.targetUserName}` : ''}`;
          const sourceLabel = fileImportSourceLabel(b.source);
          return (
            <Box
              key={b.id}
              component="li"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1.25,
                borderRadius: 1,
                bgcolor: 'surface.raised',
                border: '1px solid',
                borderColor: 'divider',
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
                  {b.fileName}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {formatWhen(b.uploadedAt)}{sourceLabel ? ` · ${sourceLabel}` : ''} · {pluralActivities(b.rowsImported)}{who}
                </Typography>
              </Box>
              {b.undoneAt ? (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Undone {formatWhen(b.undoneAt)}
                </Typography>
              ) : b.canUndo ? (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<UndoIcon />}
                  disabled={busy}
                  onClick={() => setPending(b)}
                >
                  Undo
                </Button>
              ) : b.editedRows > 0 ? (
                <Tooltip title={`${b.editedRows} ${b.editedRows === 1 ? 'row was' : 'rows were'} edited since this import, so it can’t be undone.`}>
                  {/* Focusable so keyboard users can reach the explanation too. */}
                  <Typography component="span" tabIndex={0} variant="caption" sx={{ color: 'text.secondary', borderRadius: 0.5 }}>
                    Edited since import
                  </Typography>
                </Tooltip>
              ) : null}
            </Box>
          );
        })}
      </Box>

      <ConfirmDialog
        open={pending !== null}
        title="Undo this import?"
        message={pending
          ? `Remove ${pluralActivities(pending.rowsImported)} imported from “${pending.fileName}” on ${formatWhen(pending.uploadedAt)}.`
          : ''}
        confirmLabel="Undo import"
        loading={busy}
        onConfirm={() => void confirmUndo()}
        onClose={() => setPending(null)}
      />
    </Box>
  );
}
